import { Vector } from '../shared/vector.ts';

import type { Entity, EntityId } from '../shared/entities.ts';
import {
  SMOOTHING_HALF_LIFE,
  TELEPORT_DISTANCE,
} from '../shared/parameters.ts';
import type { World } from '../shared/world.ts';

/**
 * Snapshots land 30 times a second and the screen redraws 60 or 120, so an
 * entity drawn at its last known position jumps. This walks the drawn position
 * towards the known one instead, framerate-independently.
 */
export function approach(
  current: Vector,
  target: Vector,
  delta: number,
  halfLife = SMOOTHING_HALF_LIFE,
  teleportDistance = TELEPORT_DISTANCE,
): Vector {
  if (current.distance(target) > teleportDistance) return target;

  const ratio = 1 - 2 ** (-delta / halfLife);
  return current.add(target.sub(current).scale(ratio));
}

/**
 * Where each entity is drawn, which is not where it is. The local player is
 * excluded: its position is this client's own prediction, and smoothing it
 * would put the lag back in exactly the place prediction removed it from.
 *
 * The two knobs are arguments so a test can sweep them; nothing but a test
 * passes them.
 */
export function createSmoother(
  halfLife = SMOOTHING_HALF_LIFE,
  teleportDistance = TELEPORT_DISTANCE,
) {
  const drawn = new Map<EntityId, Vector>();

  return { update, positionOf };

  function update(world: World, delta: number, excluded?: EntityId) {
    for (const entity of world.entities.values()) {
      if (entity.id === excluded) continue;

      const current = drawn.get(entity.id) ?? entity.position;

      drawn.set(
        entity.id,
        approach(current, entity.position, delta, halfLife, teleportDistance),
      );
    }

    for (const id of drawn.keys()) {
      if (!world.entities.has(id)) drawn.delete(id);
    }
  }

  function positionOf(entity: Entity) {
    return drawn.get(entity.id) ?? entity.position;
  }
}
